import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Tooltip, Icon, Select, Rate } from "antd";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import config from "appConfig";
import styles from "./manageRatingsPage.scss";
import { getRatings, updateRatingStatus } from "./ratings.ducks";
import { formatDate, api, notification } from "../../../services";
import {
  UserRatingStatusText,
  UserRatingType
} from "../../../models/enums";
import { IndexPagination } from "../../../components/IndexPagination";
import PageTitle from "../PageTitle";
import RatingsFilterForm from "./RatingsFilterForm";
import { getRatingCategoryLabel } from "../../../utils/ratingHelpers";
import { saveAs } from "file-saver";

const ManageRatingsPage = props => {
  const { page, itemPerPage, total, filters, ratings } = props;
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(filters); // preserve current filters with defaults

  const getChatLog = async (chatId) => {
    try {
      const chatLog = await api.Chat.getChatLog(chatId);
      saveAs("data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,"
        + chatLog, `chat-log-${chatId}.xlsx`);
    } catch (e) {
      console.error(e);
      notification.showError("Greška prilikom preuzimanja chat loga");
    }
  };

  const columns = [
    {
      title: "Datum",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 105,
      render: (text) => formatDate(text, config.fullTimeFormat)
    },
    {
      title: "Oglas",
      dataIndex: "code",
      key: "code",
      width: 100,
      render: (code) => (
        <Link
          to={`/admin/ad?code=${code}`}
          target="_blank"
          className={styles.link}
        >
          {code}
        </Link>
      )
    },
    {
      title: "Kupac",
      key: "buyer",
      width: 140,
      render: (_, record) => {
        const isBuyerRating = record.type === UserRatingType.buyerRating;
        const buyerEmail = isBuyerRating ? record.userEmail : record.ratedByEmail;
        const buyerId = isBuyerRating ? record.userId : record.ratedById;
        return (
          <Link
            to={`/admin/user/profile/${buyerId}`}
            target="_blank"
            className={styles.link}
            title={buyerEmail}
          >
            {buyerEmail}
          </Link>
        );
      }
    },
    {
      title: "Prodavač",
      key: "seller",
      width: 140,
      render: (_, record) => {
        const isSellerRating = record.type === UserRatingType.sellerRating;
        const sellerEmail = isSellerRating ? record.userEmail : record.ratedByEmail;
        const sellerId = isSellerRating ? record.userId : record.ratedById;
        return (
          <Link
            to={`/admin/user/profile/${sellerId}`}
            target="_blank"
            className={styles.link}
            title={sellerEmail}
          >
            {sellerEmail}
          </Link>
        );
      }
    },
    {
      title: "Kriteriji",
      key: "criteria",
      width: 280,
      render: (_, record) => {
        const isSellerRating = record.type === UserRatingType.sellerRating;
        return (
          <div>
            <div style={{ marginBottom: 4 }}>
              {isSellerRating ? getRatingCategoryLabel('adDescriptionAccuracy') : getRatingCategoryLabel('paymentAndExchangeAgreement')}:
              <Rate disabled value={isSellerRating ? record.adDescriptionAccuracy : record.paymentAndExchangeAgreement} style={{ fontSize: 12, marginLeft: 8 }} />
            </div>
            <div style={{ marginBottom: 4 }}>
              {getRatingCategoryLabel('communication')}:
              <Rate disabled value={record.communication} style={{ fontSize: 12, marginLeft: 8 }} />
            </div>
            <div>
              {getRatingCategoryLabel('honoringAgreement')}:
              <Rate disabled value={record.honoringAgreement} style={{ fontSize: 12, marginLeft: 8 }} />
            </div>
          </div>
        );
      }
    },
    {
      title: "Komentar i odgovor",
      dataIndex: "comment",
      key: "comment",
      width: 300,
      render: (comment, record) => (
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          <div>{comment}</div>
          {record.answerComment && (
            <div>
              <hr style={{ margin: "8px 0", border: "none", borderTop: "1px solid #d9d9d9" }} />
              <div><strong>Odgovor:</strong></div>
              <div>{record.answerComment}</div>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      fixed: "right",
      render: (status, record) => (
        <Select
          value={status}
          style={{ width: "100%" }}
          onChange={(value) => handleStatusChange(record.id, value)}
        >
          {UserRatingStatusText.map(statusOption => (
            <Select.Option key={statusOption.value} value={statusOption.value}>
              {statusOption.name}
            </Select.Option>
          ))}
        </Select>
      )
    },
    {
      title: "Akcije",
      key: "actions",
      align: "center",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <div
          style={{
            display: "inline-block",
            marginTop: "5px"
          }}
        >
          <Tooltip placement="topLeft" title="Skini Chat Log">
            <Button
              onClick={() => getChatLog(record.chatId)}
            >
              <Icon type="download" />
            </Button>
          </Tooltip>
        </div>
      )
    }
  ];

  useEffect(() => {
    api.UserRating.pageAccessed().catch(() => {});
    props.getRatings(filters, page, itemPerPage);
  }, []);

  const onPageChange = page => {
    props.getRatings(currentFilters, page, itemPerPage);
  };

  const handleSearch = filters => {
    setCurrentFilters(filters);
    props.getRatings(filters, 1, itemPerPage);
  };

  const handleStatusChange = (ratingId, newStatus) => {
    const statusObj = UserRatingStatusText.find(s => s.value === newStatus);

    Modal.confirm({
      title: `Želite li postaviti status ocjene na "${statusObj ? statusObj.name : ""}"?`,
      // @ts-ignore
      centered: true,
      iconType: "NaN",
      onOk: () => {
        setLoading(true);
        props.updateRatingStatus(ratingId, newStatus, ratings, () => {
          props.getRatings(filters, page, itemPerPage).then(() => {
            setLoading(false);
            notification.show("Status je uspješno promijenjen");
          });
        });
      }
    });
  };

  return (
    <React.Fragment>
      <PageTitle title={"Ocjene"} />
      <RatingsFilterForm
        value={filters}
        onSubmit={handleSearch}
        className=""
      />
      <Table
        dataSource={ratings}
        columns={columns}
        pagination={false}
        bordered
        rowKey="id"
        className="admin-table"
        loading={loading}
        scroll={{ x: "max-content" }}
      />
      {ratings.length > 0 ? (
        <IndexPagination
          total={total}
          page={page}
          itemPerPage={itemPerPage}
          onPageChange={onPageChange}
        />
      ) : null}
    </React.Fragment>
  );
};

export default connect(
  state => ({
    // @ts-ignore
    ...state.admin.ratings
  }),
  {
    getRatings,
    updateRatingStatus
  }
)(ManageRatingsPage);



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageRatingsPage/ManageRatingsPage.tsx